/**
 * Turns schedule events into the two lists `react-big-calendar`'s resource
 * mode wants: one row per person, and one event per (shift, person) pair.
 *
 * The month view draws a shift once; the "By employee" view draws it once in
 * every row it touches, so a shift with three people becomes three events
 * that all keep the shift's own `id`. Clicking any of them still resolves
 * back to the one schedule.
 */

/** Only the parts of a person this needs, so any shape carrying a name fits. */
export type ResourcePerson = {
  id?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

/**
 * The minimum an event has to carry. `supervisors` is separate from
 * `employees` because the server hands the two back as disjoint lists: see
 * `createScheduleResponseDto`, which partitions a schedule's users by the
 * `isSupervisor` flag on the link rows.
 */
export type SchedulableEvent = {
  employees?: ResourcePerson[] | null;
  /**
   * Required, though it may be null or undefined: `CalendarCustomEventType`
   * does not carry supervisors yet, and an optional key would let a caller
   * pass today's events, quietly get no supervisor rows, and still typecheck.
   * Making the key required forces the wiring task to plumb it through.
   */
  supervisors: ResourcePerson[] | null | undefined;
};

/**
 * One copy of an event, pinned to one person's row.
 *
 * `asSupervisor` is a property of this pairing, not of the person: the same
 * employee can supervise one shift and work another as a technician, so the
 * flag belongs here and not on the row.
 */
export type ResourceEvent<E extends SchedulableEvent> = E & {
  resourceId: string;
  asSupervisor: boolean;
};

/** A row in the resource timeline. */
export type ScheduleResource = {
  id: string;
  title: string;
};

type CrewMember = { id: string; title: string; asSupervisor: boolean };

const displayName = (person: ResourcePerson): string =>
  [person.firstName, person.lastName]
    .filter((part) => typeof part === "string" && part.trim())
    .map((part) => (part as string).trim())
    .join(" ");

/**
 * Everyone this event commits, supervisors first so that a person who somehow
 * appears in both lists is counted once, as a supervisor. Anyone without an
 * id is dropped: a row has to be addressable, and an id is the only stable
 * handle we have.
 */
const crewOf = (event: SchedulableEvent | null | undefined): CrewMember[] => {
  const crew: CrewMember[] = [];
  const seen = new Set<string>();

  const take = (people: unknown, asSupervisor: boolean) => {
    if (!Array.isArray(people)) {
      return;
    }

    for (const person of people) {
      if (!person || typeof person !== "object") {
        continue;
      }

      const id = (person as ResourcePerson).id;

      if (typeof id !== "string" || !id || seen.has(id)) {
        continue;
      }

      seen.add(id);
      crew.push({
        id,
        title: displayName(person as ResourcePerson),
        asSupervisor,
      });
    }
  };

  take(event?.supervisors, true);
  take(event?.employees, false);

  return crew;
};

/** An empty or absent selection means everyone, not no one. */
const selectionFilter = (
  selectedEmployeeIds?: string[] | null
): ((id: string) => boolean) => {
  const selected = new Set(
    (Array.isArray(selectedEmployeeIds) ? selectedEmployeeIds : []).filter(
      (id): id is string => typeof id === "string" && !!id
    )
  );

  return selected.size ? (id: string) => selected.has(id) : () => true;
};

/**
 * Fans each event out into one event per person it commits.
 *
 * An event with no crew yields nothing: a shift nobody is on belongs in no
 * row, and inventing an "Unassigned" row would put a person-shaped thing in
 * the employee list.
 */
export const toResourceEvents = <E extends SchedulableEvent>(
  events?: E[] | null,
  selectedEmployeeIds?: string[] | null
): ResourceEvent<E>[] => {
  const isSelected = selectionFilter(selectedEmployeeIds);
  const fanned: ResourceEvent<E>[] = [];

  for (const event of Array.isArray(events) ? events : []) {
    if (!event || typeof event !== "object") {
      continue;
    }

    for (const member of crewOf(event)) {
      if (!isSelected(member.id)) {
        continue;
      }

      // Spread rather than mutate: the caller's events stay the ones the
      // month view and `onEventDrop` are still working from.
      fanned.push({
        ...event,
        resourceId: member.id,
        asSupervisor: member.asSupervisor,
      });
    }
  }

  return fanned;
};

/**
 * The row list: each person once, ordered by name.
 *
 * Name order rather than first-seen order because `onEventDrop` re-appends
 * the dragged event to the end of the list, so first-seen order would
 * reshuffle the rows after every drag.
 *
 * Two different people with the same name stay two rows - they are different
 * ids and merging them would put one person's shifts on another's line. Their
 * titles will read identically; telling them apart on screen is the UI's
 * problem, not this transform's. Ties are broken by id so the order is stable
 * rather than dependent on the sort's own stability.
 *
 * Rows carry no supervisor flag: supervising is per shift, so a row-level
 * flag would be a lie for anyone who does both. The flag is on the events.
 */
export const toResources = <E extends SchedulableEvent>(
  events?: E[] | null,
  selectedEmployeeIds?: string[] | null
): ScheduleResource[] => {
  const isSelected = selectionFilter(selectedEmployeeIds);
  const titles = new Map<string, string>();

  for (const event of Array.isArray(events) ? events : []) {
    if (!event || typeof event !== "object") {
      continue;
    }

    for (const member of crewOf(event)) {
      if (!isSelected(member.id)) {
        continue;
      }

      const known = titles.get(member.id);

      // One id can arrive under more than one spelling. Taking the smallest
      // non-empty name keeps the row's label from flipping with event order.
      if (known === undefined) {
        titles.set(member.id, member.title);
      } else if (member.title && (!known || member.title < known)) {
        titles.set(member.id, member.title);
      }
    }
  }

  return Array.from(titles, ([id, title]) => ({
    id,
    // A person with no name at all still needs a row label; the id is the
    // only thing left to show.
    title: title || `Employee ${id}`,
  })).sort(byRowTitle);
};

/** Row order: by name, then by id so equal names do not shuffle. */
const byRowTitle = (a: ScheduleResource, b: ScheduleResource): number =>
  a.title.localeCompare(b.title, undefined, { sensitivity: "base" }) ||
  a.id.localeCompare(b.id);

/**
 * Gives every explicitly selected employee a row, even one with nothing on.
 *
 * `toResources` can only name the people its events mention, so someone with
 * no shift in the window drops out of the list entirely - and their absence
 * reads as "not selected" rather than "free", which is the question the view
 * is for. Naming them needs a second source, the employee list the filter's
 * own options come from.
 *
 * Only an explicit selection gets this treatment. With nothing selected the
 * view means "everyone who is working", and padding it out to the whole
 * payroll would bury the shifts under blank lanes.
 *
 * A selected id the list does not know stays out: a row with no name and no
 * shifts carries nothing at all.
 */
export const withEmptyLanes = (
  resources: ScheduleResource[],
  selectedEmployeeIds?: string[] | null,
  people?: ResourcePerson[] | null
): ScheduleResource[] => {
  const selected = (
    Array.isArray(selectedEmployeeIds) ? selectedEmployeeIds : []
  ).filter((id): id is string => typeof id === "string" && !!id);

  // Not what enforces "nothing selected means no padding" - the loop below
  // walks `selected` and so adds nothing anyway. This only spares the roster
  // scan in the common case, which is why removing it changes no test.
  if (!selected.length) {
    return resources;
  }

  const known = new Map<string, string>();

  for (const person of Array.isArray(people) ? people : []) {
    const id = person?.id;

    if (typeof id === "string" && id && !known.has(id)) {
      known.set(id, displayName(person));
    }
  }

  const placed = new Set(resources.map((resource) => resource.id));
  const added: ScheduleResource[] = [];

  for (const id of selected) {
    if (placed.has(id)) {
      continue;
    }

    placed.add(id);
    const title = known.get(id);

    if (title === undefined) {
      continue;
    }

    added.push({ id, title: title || `Employee ${id}` });
  }

  return added.length ? [...resources, ...added].sort(byRowTitle) : resources;
};
